import React, { useEffect, useState } from "react";
import AdminLayout from "../components/AdminLayout";
import { collection, getDocs, deleteDoc, doc, query, orderBy } from "firebase/firestore";
import { db } from "../../firebase/config";
import { FiTrash2, FiStar, FiEye } from "react-icons/fi";

const Reviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewing, setViewing] = useState(null);

  const fetch = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(query(collection(db, "reviews"), orderBy("createdAt", "desc")));
      setReviews(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (e) {}
    setLoading(false);
  };

  useEffect(() => { fetch(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this review?")) return;
    await deleteDoc(doc(db, "reviews", id));
    fetch();
  };

  return (
    <AdminLayout>
      <div className="max-w-6xl">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-extrabold text-black">Product Reviews</h1>
            <p className="text-sm text-gray-400">{reviews.length} review{reviews.length !== 1 ? "s" : ""}</p>
          </div>
          <button onClick={fetch} className="px-4 py-2 border border-gray-300 rounded-lg text-[12px] font-semibold hover:border-[#e84fa5] transition">Refresh</button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><div className="w-8 h-8 border-4 border-[#e84fa5] border-t-transparent rounded-full animate-spin" /></div>
        ) : reviews.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-100 p-10 text-center text-gray-400 text-[13px]">No reviews yet.</div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <th className="px-4 py-3 text-[11px] font-extrabold text-gray-500 uppercase tracking-wide">#</th>
                  <th className="px-4 py-3 text-[11px] font-extrabold text-gray-500 uppercase tracking-wide">Product</th>
                  <th className="px-4 py-3 text-[11px] font-extrabold text-gray-500 uppercase tracking-wide">User</th>
                  <th className="px-4 py-3 text-[11px] font-extrabold text-gray-500 uppercase tracking-wide">Rating</th>
                  <th className="px-4 py-3 text-[11px] font-extrabold text-gray-500 uppercase tracking-wide">Comment</th>
                  <th className="px-4 py-3 text-[11px] font-extrabold text-gray-500 uppercase tracking-wide">Date</th>
                  <th className="px-4 py-3 text-[11px] font-extrabold text-gray-500 uppercase tracking-wide text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {reviews.map((r, i) => (
                  <tr key={r.id} className="border-b border-gray-50 hover:bg-gray-50/50 transition">
                    <td className="px-4 py-3 text-[12px] text-gray-400">{i + 1}</td>
                    <td className="px-4 py-3 text-[12px] font-semibold text-black max-w-[180px] truncate">{r.productTitle || "—"}</td>
                    <td className="px-4 py-3 text-[12px] text-gray-700">{r.name}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center text-[#f5a623] text-[11px]">
                        {[1, 2, 3, 4, 5].map((s) => (
                          <FiStar key={s} fill={s <= r.rating ? "#f5a623" : "none"} stroke="#f5a623" size={12} />
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-[12px] text-gray-600 max-w-[220px] truncate">{r.comment}</td>
                    <td className="px-4 py-3 text-[11px] text-gray-400 whitespace-nowrap">{r.createdAt?.toDate?.().toLocaleDateString() || "—"}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => setViewing(r)}
                          className="p-1.5 text-gray-400 hover:text-[#e84fa5] hover:bg-pink-50 rounded-lg transition" title="View">
                          <FiEye size={14} />
                        </button>
                        <button onClick={() => handleDelete(r.id)}
                          className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition" title="Delete">
                          <FiTrash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* View Modal */}
        {viewing && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => setViewing(null)}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden" onClick={(e) => e.stopPropagation()}>
              <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
                <h3 className="text-[15px] font-extrabold text-black">Review Details</h3>
                <button onClick={() => setViewing(null)} className="text-gray-400 hover:text-gray-700 text-[18px] leading-none">&times;</button>
              </div>
              <div className="px-6 py-5 space-y-4">
                <div>
                  <p className="text-[10px] font-extrabold text-gray-400 uppercase tracking-wide mb-1">Product</p>
                  <p className="text-[13px] font-semibold text-black">{viewing.productTitle || "—"}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-[10px] font-extrabold text-gray-400 uppercase tracking-wide mb-1">Name</p>
                    <p className="text-[13px] text-black">{viewing.name}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-extrabold text-gray-400 uppercase tracking-wide mb-1">Email</p>
                    <p className="text-[13px] text-black">{viewing.email}</p>
                  </div>
                </div>
                <div>
                  <p className="text-[10px] font-extrabold text-gray-400 uppercase tracking-wide mb-1">Rating</p>
                  <div className="flex items-center text-[#f5a623] text-[15px] gap-0.5">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <FiStar key={s} fill={s <= viewing.rating ? "#f5a623" : "none"} stroke="#f5a623" />
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-[10px] font-extrabold text-gray-400 uppercase tracking-wide mb-1">Comment</p>
                  <p className="text-[13px] text-gray-700 leading-relaxed">{viewing.comment}</p>
                </div>
                {viewing.createdAt?.toDate && (
                  <div>
                    <p className="text-[10px] font-extrabold text-gray-400 uppercase tracking-wide mb-1">Submitted On</p>
                    <p className="text-[13px] text-gray-500">{viewing.createdAt.toDate().toLocaleString()}</p>
                  </div>
                )}
              </div>
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end">
                <button onClick={() => setViewing(null)} className="px-6 py-2 bg-black text-white text-[11px] font-bold uppercase rounded-lg hover:bg-gray-800 transition">Close</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default Reviews;
